import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuthStore } from '../store'
import { useLogin } from '../hooks'

export const LoginPage: React.FC = () => {
  const { error, isAuthenticated, user } = useAuthStore()
  const { login } = useLogin()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const loginInitiatedRef = useRef(false)
  const [isAutoRedirecting, setIsAutoRedirecting] = useState(() => {
    const fromLogoutUrl = searchParams.get('from') === 'logout'
    const fromLogoutStorage = localStorage.getItem('just_logged_out') === 'true'
    const fromLogout = fromLogoutUrl || fromLogoutStorage
    
    console.log('[LoginPage] 初始化:', {
      from: searchParams.get('from'),
      fromLogoutUrl,
      fromLogoutStorage,
      fromLogout,
      search: window.location.search
    })
    return !fromLogout
  })
  
  // 在 effect 中清除标记，避免 StrictMode 双渲染问题
  useEffect(() => {
    if (localStorage.getItem('just_logged_out') === 'true') {
      localStorage.removeItem('just_logged_out')
      console.log('[LoginPage] 已清除 just_logged_out 标记')
    }
  }, [])

  useEffect(() => {
    if (isAuthenticated && user) {
      // 交由 StartupRedirect 决策落地页（移动端/兜底 → /suji 速记模式）
      navigate('/', { replace: true })
      return
    }

    // 如果 URL 中有 OAuth 回调参数（code + state）或回调正在处理中，说明回调正在处理
    // 不要自动触发新的登录，否则会中断正在进行的回调处理
    const params = new URLSearchParams(window.location.search)
    const hasCallbackParams = params.has('code') && params.has('state')
    const isCallbackProcessing = sessionStorage.getItem('oauth_callback_processing') === 'true'

    if (isAutoRedirecting && !error && !loginInitiatedRef.current && !hasCallbackParams && !isCallbackProcessing) {
      loginInitiatedRef.current = true
      console.log('[LoginPage] 自动跳转到 Casdoor 登录页')
      login()
    }
  }, [isAuthenticated, isAutoRedirecting, error, login, navigate])

  // 错误状态
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full mx-4">
          <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full mx-auto mb-4 flex items-center justify-center">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">登录失败</h1>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => {
                setIsAutoRedirecting(true)
                login()
              }}
              className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              重试
            </button>
          </div>
        </div>
      </div>
    )
  }

  // 自动跳转中状态
  if (isAutoRedirecting) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">正在跳转到登录页...</h1>
          <p className="text-gray-600 mb-2">请稍候</p>
          <p className="text-gray-500 text-sm mb-4">请检查网络连接或等待云同步和认证服务恢复</p>
          <button
            onClick={() => setIsAutoRedirecting(false)}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
          >
            取消
          </button>
        </div>
      </div>
    )
  }

  // 用户取消自动跳转后的手动登录界面
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="max-w-md w-full mx-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-blue-600 rounded-xl mx-auto mb-4 flex items-center justify-center">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">欢迎回来</h1>
          <p className="text-gray-600 mb-6">登录以继续访问您的笔记</p>
          <button
            onClick={() => {
              setIsAutoRedirecting(true)
              login()
            }}
            className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            使用 Casdoor 登录
          </button>
        </div>
      </div>
    </div>
  )
}
